import { kebabCase } from 'lodash';
import { ComponentPropsWithRef, Ref, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../../data/utils/string.utils';
import { BaseInputIcon } from './base-input';
import { BaseTextArea } from './base-textarea';

type InputTextAreaProps = ComponentPropsWithRef<typeof BaseTextArea> & {
  type?: string;
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  icon?: BaseInputIcon;
  error?: string | { error: string; [key: string]: string };
};

export function InputTextArea({
  testId,
  className,
  label,
  description,
  prefix,
  suffix,
  icon = BaseInputIcon.NONE,
  error,
  ...props
}: InputTextAreaProps) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-textarea', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--textarea${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${props.disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        {prefix && (
          <span
            data-test-id={getTestId('prefix')}
            className="form-control-prefix"
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            data-test-id={getTestId('suffix')}
            className="form-control-suffix"
          >
            {suffix}
          </span>
        )}

        {icon !== BaseInputIcon.NONE && (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${kebabCase(icon)}`}
          />
        )}

        <BaseTextArea
          {...props}
          testId={getTestId('input')}
          ref={(props.ref as Ref<HTMLTextAreaElement>) ?? undefined}
        />

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
